/// <amd-module name="Feed/Connect/Redux/ConnectToFeed/ConnectToFeedSagas" />
import { SagaIterator } from "redux-saga";
import { effects } from "redux/redux-saga.compat";
import { ActionWithPayload } from "redux/Utils/ActionHelper";

import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssPageService } from "VSS/Platform/Page";
import * as Storage from "VSS/Platform/Util/Storage";

import { getSelectedUxFeedView, isMember } from "Feed/Core/Redux/FeedHub";
import { IUxFeedView } from "Feed/Core/UxFeedView";

import { Ivy } from "../../Protocols/Ivy";
import * as Maven from "../../Protocols/Maven";
import { npm } from "../../Protocols/npm";
import * as NuGet from "../../Protocols/NuGet";
import * as Python from "../../Protocols/Python";
import { UniversalPackages } from "../../Protocols/Upack";
import { ITool, ToolId } from "../../Tool";
import { ConnectToFeedActions, ConnectToFeedActionTypes } from "./ConnectToFeedActions";

export const dismissedToolsKey = "ms.feed.feed-connect.dismissedTools";

const defaultTools: ITool[] = [
    NuGet.DotNet,
    NuGet.NuGetExe,
    NuGet.VisualStudio,
    npm,
    Maven.Maven,
    Maven.Gradle,
    Python.pip,
    Python.twine,
    UniversalPackages,
    Ivy
];

export function* initializeTools(): SagaIterator {
    const pageContext = ((yield effects.getContext("pageContext")) as unknown) as IVssPageContext;

    let feed: IUxFeedView = (yield effects.select(getSelectedUxFeedView)) as any;
    // wait until feed is defined
    while (feed == undefined) {
        yield effects.take();
        feed = (yield effects.select(getSelectedUxFeedView)) as any;
    }

    const userIsMember = (yield effects.select(isMember)) as any;

    const pageData = pageContext.getService<IVssPageService>("IVssPageService").getData();
    let visibleTools: ITool[] = defaultTools.filter(tool => {
        const hasValidAction = tool.supportsGet(feed) || tool.supportsPush(feed);
        const isEnvironmentSupported = pageData ? tool.isEnvironmentSupported(pageData) : true;
        const visibleToUser = userIsMember || tool.allowNonMembers;
        const featureEnabled = !tool.featureFlag || isFeatureFlagEnabled(pageContext, tool.featureFlag);
        return hasValidAction && visibleToUser && featureEnabled && isEnvironmentSupported;
    });

    // Wait to generate the list of tools until we have the asset data from the contribution
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    yield effects.call([contributionService, contributionService.getDataAsync], "ms.feed.feed-connect.view");

    yield effects.put(ConnectToFeedActions.toolsInitialized(visibleTools));
}

export function* initializeMessageDismissedTools(): SagaIterator {
    const storedMessageDismissedTools: ToolId[] =
        (yield effects.call(Storage.tryGetLocalStorageObject, dismissedToolsKey)) || [];
    yield effects.put(ConnectToFeedActions.initializeMessageDismissedTools(storedMessageDismissedTools));
}

export function* handleMessageDismissed(
    action: ActionWithPayload<ConnectToFeedActionTypes.DismissFirstTimeMessage, ToolId>
): SagaIterator {
    const dismissedToolIds: ToolId[] = (yield effects.call(Storage.tryGetLocalStorageObject, dismissedToolsKey)) || [];

    const toolId: ToolId = action.payload;

    if (dismissedToolIds.indexOf(toolId) == -1) {
        dismissedToolIds.push(toolId);
        yield effects.call(Storage.trySetLocalStorageObject, dismissedToolsKey, dismissedToolIds);
    }
}

export default function* rootSaga(): SagaIterator {
    yield effects.all([effects.call(initializeMessageDismissedTools), effects.call(initializeTools)]);
    yield effects.takeEvery(ConnectToFeedActionTypes.DismissFirstTimeMessage, handleMessageDismissed);
}
